import { Badge, Button, Group, NumberInput, Paper, Stack, Text, Title } from '@mantine/core';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition, SpecState } from '../qualityFormulaTypes';
import type { QualityFormulaStore } from '../store/qualityFormulaStore';

import { toNumberOrDefault } from '../numberInputValue';
import { CONTRACT_PRICE_COMPONENTS, CONTRACT_UNIT_OF_MEASURE } from '../qualityFormulaMetadata';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { AnimatedItem } from './AnimatedItem';
import { getDeemedPriceIfApplicable, getSelectedPriceComponentKeysJoined } from './utils/formulaTestingPanelSelectors';

interface FormulaTestingPanelProps {
  description: string;
  enabledSpecs: SpecDefinition[];
  // Contract Entry's "Reset" button sits next to the Test button; the Template Editor has no
  // equivalent, so this is the only slot left open rather than baking Reset into this component.
  extraActions?: React.ReactNode;
  priceLabel?: string;
  quantityLabel?: string;
  testButtonLabel: string;
  title: string;
}

interface SpecPriceComponentsInputsProps {
  components: typeof CONTRACT_PRICE_COMPONENTS;
  onChange: (key: string, value: number) => void;
  specLabel: string;
  /** Applied by `AnimatedItem` to fade this spec's block in when it's newly added — see its
   * own doc comment. */
  style?: React.CSSProperties;
  values: Record<string, number>;
}

// One spec's badge + its selected components' editable inputs — split out purely so the spec tag
// and its inputs read as one reusable unit instead of being inlined into FormulaTestingPanel's own
// render. The badge sits on its own row (like a section label) rather than inline with the inputs,
// matching how the Actual Value fields below use a badge as each input's own label.
const SpecPriceComponentsInputs: React.FC<SpecPriceComponentsInputsProps> = ({
  components,
  onChange,
  specLabel,
  style,
  values,
}) => (
  <Paper bg="var(--mantine-color-default-hover)" p="xs" radius="md" style={style} w="fit-content" withBorder>
    <Stack gap={4}>
      <Badge size="sm" variant="light" w="fit-content">
        {specLabel}
      </Badge>
      <Group gap={6} wrap="wrap">
        {components.map(component => (
          <NumberInput
            key={component.key}
            label={component.label}
            onChange={value => {
              onChange(component.key, toNumberOrDefault(value, 0));
            }}
            value={values[component.key]}
            w={110}
          />
        ))}
      </Group>
    </Stack>
  </Paper>
);

/**
 * Builds a flat, primitive-only, spec-keyed record by applying `pick` to each of `enabledSpecs`'
 * live `SpecState` — the shared shape behind both price-basis derivations in `FormulaTestingPanel`
 * (Price Components keys, Deemed Price), so the same `useShallow`-safe reduce only lives once.
 * `useShallow` only bails out of a re-render when the selector's own output is shallowly equal to
 * last time, which only holds for primitives — `pick` must return a primitive (or `undefined` when
 * it doesn't apply to a given spec; see `getSelectedPriceComponentKeysJoined`/
 * `getDeemedPriceIfApplicable`), never a freshly-built array/object.
 */
const useSpecStatePrimitiveBySpec = (
  enabledSpecs: SpecDefinition[],
  pick: (specState: SpecState) => string | undefined,
): Record<string, string | undefined> =>
  useQualityFormulaStore(
    useShallow((store: QualityFormulaStore) =>
      enabledSpecs.reduce<Record<string, string | undefined>>((acc, spec) => {
        acc[spec.key] = pick(store.specStates[spec.key]);
        return acc;
      }, {}),
    ),
  );

/**
 * The "enter sample values, run the calculate API, review the button" section shared by Contract
 * Entry and the Template Manager's Formula Testing panel — both need the exact same
 * quantity/price/actual-value inputs wired to the same quality-formula store's preview call, so
 * this is the single place that skeleton lives instead of being copy-pasted per page. Reads its
 * own test-value/preview state straight from the store (rather than taking it as props) since
 * both call sites were only ever passing their own `useQualityFormulaStore` selectors straight
 * back down — only genuinely page-specific config (labels, copy, the Reset slot) remains a prop.
 */
export const FormulaTestingPanel: React.FC<FormulaTestingPanelProps> = ({
  description,
  enabledSpecs,
  extraActions,
  priceLabel = 'Contract Price',
  quantityLabel = 'Delivered Quantity',
  testButtonLabel,
  title,
}) => {
  const {
    actualValues,
    deliveredQuantity,
    isTesting,
    runPreview,
    setActualValues,
    setDeliveredQuantity,
    setTestComponentValue,
    setTestDeemedPriceOverride,
    setTradePrice,
    testComponentValues,
    testDeemedPriceOverrides,
    tradePrice,
  } = useQualityFormulaStore(
    useShallow(store => ({
      actualValues: store.actualValues,
      deliveredQuantity: store.deliveredQuantity,
      isTesting: store.isTesting,
      runPreview: store.runPreview,
      setActualValues: store.setActualValues,
      setDeliveredQuantity: store.setDeliveredQuantity,
      setTestComponentValue: store.setTestComponentValue,
      setTestDeemedPriceOverride: store.setTestDeemedPriceOverride,
      setTradePrice: store.setTradePrice,
      testComponentValues: store.testComponentValues,
      testDeemedPriceOverrides: store.testDeemedPriceOverrides,
      tradePrice: store.tradePrice,
    })),
  );

  const selectedKeysBySpec = useSpecStatePrimitiveBySpec(enabledSpecs, getSelectedPriceComponentKeysJoined);

  // Per-spec components shown in the Price Components section below, for specs scaling against
  // Price Components instead of the shared Contract Price — every enabled spec gets an entry
  // (possibly empty) so indexing below never needs an undefined check.
  const priceComponentsBySpec = useMemo(
    () =>
      Object.entries(selectedKeysBySpec).reduce<Record<string, typeof CONTRACT_PRICE_COMPONENTS>>(
        (acc, [specKey, keys]) => {
          const selectedKeys = keys ? keys.split(',') : [];
          acc[specKey] = CONTRACT_PRICE_COMPONENTS.filter(component => selectedKeys.includes(component.key));
          return acc;
        },
        {},
      ),
    [selectedKeysBySpec],
  );
  const specsWithPriceComponents = enabledSpecs.filter(spec => priceComponentsBySpec[spec.key].length > 0);

  // Only ever used as the displayed input's fallback (via `testDeemedPriceOverrides`) — testing a
  // formula must never change what's actually configured for a spec, only read it as a starting
  // point (see store's own doc comment).
  const deemedPriceBySpec = useSpecStatePrimitiveBySpec(enabledSpecs, getDeemedPriceIfApplicable);
  const specsWithDeemedPrice = enabledSpecs.filter(spec => deemedPriceBySpec[spec.key] !== undefined);

  return (
    <Stack aria-label="Test formulas with sample values" component="section" gap="md">
      <Stack gap={4}>
        <Title order={2} size="h4">
          {title}
        </Title>
        <Text c="dimmed" fz="sm" maw={640}>
          {description}
        </Text>
      </Stack>

      <Group align="flex-end" gap="sm" wrap="wrap">
        <NumberInput
          label={quantityLabel}
          min={0.0001}
          onChange={value => {
            setDeliveredQuantity(toNumberOrDefault(value, 0));
          }}
          suffix={` ${CONTRACT_UNIT_OF_MEASURE}`}
          value={deliveredQuantity}
          w={160}
        />
        <NumberInput
          label={priceLabel}
          onChange={value => {
            setTradePrice(toNumberOrDefault(value, 0));
          }}
          value={tradePrice}
          w={160}
        />
        {specsWithDeemedPrice.map(spec => (
          <AnimatedItem key={spec.key} visible>
            {styles => (
              <Stack gap={4} style={styles}>
                <Badge size="sm" variant="light" w="fit-content">
                  {spec.label}
                </Badge>
                <NumberInput
                  label="Deemed Price"
                  onChange={value => {
                    setTestDeemedPriceOverride(spec.key, toNumberOrDefault(value, 0));
                  }}
                  value={testDeemedPriceOverrides[spec.key] ?? Number(deemedPriceBySpec[spec.key])}
                  w={140}
                />
              </Stack>
            )}
          </AnimatedItem>
        ))}
      </Group>

      {specsWithPriceComponents.length > 0 ? (
        <Stack gap="sm">
          <Text c="dimmed" fw={600} fz={11} lts={0.3} tt="uppercase">
            Price Components
          </Text>
          <Group gap="sm" wrap="wrap">
            {specsWithPriceComponents.map(spec => (
              <AnimatedItem key={spec.key} visible>
                {styles => (
                  <SpecPriceComponentsInputs
                    components={priceComponentsBySpec[spec.key]}
                    onChange={setTestComponentValue}
                    specLabel={spec.label}
                    style={styles}
                    values={testComponentValues}
                  />
                )}
              </AnimatedItem>
            ))}
          </Group>
        </Stack>
      ) : null}

      <Stack gap="sm">
        <Text c="dimmed" fw={600} fz={11} lts={0.3} tt="uppercase">
          Actual Value{enabledSpecs.length === 1 ? '' : 's'}
        </Text>
        <Paper bg="var(--mantine-color-default-hover)" maw="80%" p="xs" radius="md" w="fit-content" withBorder>
          <Group gap="md" wrap="wrap">
            {enabledSpecs.map(spec => (
              <AnimatedItem key={spec.key} visible>
                {styles => (
                  <NumberInput
                    label={
                      <Badge mb={4} size="sm" variant="light" w="fit-content">
                        {spec.label}
                      </Badge>
                    }
                    onChange={value => {
                      setActualValues(current => ({ ...current, [spec.key]: toNumberOrDefault(value, 0) }));
                    }}
                    placeholder="e.g. 9.25"
                    style={styles}
                    value={actualValues[spec.key]}
                    w={160}
                  />
                )}
              </AnimatedItem>
            ))}
          </Group>
        </Paper>
      </Stack>

      <Group>
        <Button
          color="blue.8"
          loading={isTesting}
          onClick={() => {
            void runPreview();
          }}
          w="fit-content"
        >
          {testButtonLabel}
        </Button>
        {extraActions}
      </Group>
    </Stack>
  );
};
